import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Shield, Plus, Edit, Trash2, ArrowUpRight,
  DollarSign, Settings, Filter, Search, Star,
  AlertCircle, X, Award, Check, Building2,
  Calculator, ChevronDown, LockIcon, UnlockIcon,
  Clock, FileText, PercentIcon, MapPin, Globe,
  Tag, ChevronRight, Zap, Calendar, ChevronLeft, ChevronRight as ChevronRightIcon
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import QuotePricingForm from "./QuotePricingForm";
import CategoryDropdown from "../../components/ui/CategoryDropdown";
import AdminRegionDropdown from "../../components/ui/AdminRegionDropdown";
import {
  fetchQuotePricings,
  fetchQuotePricingById,
  createQuotePricing,
  updateQuotePricing,
  deleteQuotePricing
} from "../../redux/Slices/AdminQuotePricingSlice";
import { RootState } from "../../redux/store";
import type { QuotePricingModel, QuotePricingRegion } from "../../modules/shared/model/admin-quote-pricing.interfaces";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";

const TabButton = ({ active, label, icon: Icon, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${active
      ? "bg-blue-100 text-blue-700"
      : "text-gray-600 hover:bg-gray-100"
      }`}
  >
    <Icon className="h-4 w-4" />
    <span>{label}</span>
  </button>
);

const QuotePricing = () => {
  const dispatch = useDispatch();
  const {
    quotePricings,
    quotePricingsLoading,
    quotePricingsError,
    quotePricingsTotalCount,
    quotePricingsCurrentPage,
    quotePricingsHasNextPage,
    quotePricingsHasPreviousPage,
    quotePricingsSearchQuery
  } = useSelector((state: RootState) => (state as any).adminQuotePricings || {
    quotePricings: [],
    quotePricingsLoading: false,
    quotePricingsError: null,
    quotePricingsTotalCount: 0,
    quotePricingsCurrentPage: 1,
    quotePricingsHasNextPage: false,
    quotePricingsHasPreviousPage: false,
    quotePricingsSearchQuery: ""
  });

  const [activeTab, setActiveTab] = useState("pricing");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRegion, setSelectedRegion] = useState<any>(null);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState<QuotePricingModel | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Debug logging
  console.log("Component: Current state", {
    quotePricings,
    quotePricingsLoading,
    quotePricingsError,
    quotePricingsTotalCount
  });

  // Load data on component mount
  useEffect(() => {
    console.log("Component: Fetching quote pricings with page:", currentPage);
    dispatch(fetchQuotePricings({ page: currentPage, limit: 10 }) as any);
  }, [dispatch, currentPage]);

  // Handle search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch(fetchQuotePricings({
        page: 1,
        limit: 10,
        search: searchQuery,
        category: selectedCategory?.id || undefined,
        region: selectedRegion?.id || undefined
      }) as any);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedCategory, selectedRegion, dispatch]);

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    dispatch(fetchQuotePricings({
      page,
      limit: 10,
      search: searchQuery,
      category: selectedCategory?.id || undefined,
      region: selectedRegion?.id || undefined
    }) as any);
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this quote pricing?")) {
      dispatch(deleteQuotePricing(id) as any);
    }
  };

  // Handle edit
  const handleEdit = (rule: QuotePricingModel) => {
    setSelectedRule(rule);
    setShowEditModal(true);
  };

  // Handle form close
  const handleFormClose = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setSelectedRule(null);
  };

  // Handle form save
  const handleFormSave = (data: any) => {
    console.log("Form saved:", data);
    handleFormClose();
    // Refresh the data
    dispatch(fetchQuotePricings({ page: currentPage, limit: 10 }) as any);
  };

  // Mock data for pricing rules (fallback)


  const PricingRuleCard = ({ rule }: { rule: QuotePricingModel }) => {
    console.log(rule, "rule");
    return (
      <Card className="p-6" style={{ textAlign: "left" }}>
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold">{rule.category?.name || "Unknown Category"}</h3>
              <Badge variant={undefined} className="bg-green-100 text-green-700">
                Active
              </Badge>
            </div>
            <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
              <Calendar className="h-4 w-4" />
              Last updated: {new Date(rule.updatedAt).toLocaleDateString()}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEdit(rule)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600"
              onClick={() => handleDelete(rule.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Regions */}
        <div className="mt-6">
          <h4 className="font-medium mb-3">Regions ({rule.regions?.length || 0})</h4>
          <div className="space-y-4" style={{ textAlign: "left" }}>
            {rule.regions?.map((region: QuotePricingRegion, index: number) => (
              <Card key={index} className="p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-medium">{region.subcategory || "Default Subcategory"}</h5>
                  <div className="flex gap-2">
                    {region.emergencyModifier && (
                      <Badge variant={undefined} className="bg-red-100 text-red-700">Emergency</Badge>
                    )}
                    {region.holidayModifier && (
                      <Badge variant={undefined} className="bg-blue-100 text-blue-700">Holiday</Badge>
                    )}
                    {region.nightModifier && (
                      <Badge variant={undefined} className="bg-purple-100 text-purple-700">Night</Badge>
                    )}
                    {region.distanceModifier && (
                      <Badge variant={undefined} className="bg-orange-100 text-orange-700">Distance</Badge>
                    )}
                  </div>
                </div>

                {/* Regional Pricing */}
                <div className="mt-4 space-y-3">
                  <div className="p-3 bg-white rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">{region.region?.name || "Unknown Region"}, {region.region?.country?.name || "Unknown Country"}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div>
                        <h6 className="text-sm text-gray-500 mb-2">Quote Pricing</h6>
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span className="text-sm">Base Price</span>
                            <Badge variant="outline">{getDynamicCurrencySymbol()}{region.baseQuotePrice || 0}</Badge>
                          </div>
                          {region.emergencyQuotePrice && region.emergencyQuotePrice > 0 && (
                            <div className="flex justify-between">
                              <span className="text-sm">Emergency Price</span>
                              <Badge variant="outline">{getDynamicCurrencySymbol()}{region.emergencyQuotePrice}</Badge>
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <h6 className="text-sm text-gray-500 mb-2">Commission Rates</h6>
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span className="text-sm">Emergency</span>
                            <Badge variant="outline">{region.regularCommission || 0}%</Badge>
                          </div>
                          {(region.regularDayCommission && region.regularDayCommission > 0) && (
                            <div className="flex justify-between">
                              <span className="text-sm">Day</span>
                              <Badge variant="outline">{region.regularDayCommission}%</Badge>
                            </div>
                          )}
                          {(region.regularNightCommission && region.regularNightCommission > 0) && (
                            <div className="flex justify-between">
                              <span className="text-sm">Night</span>
                              <Badge variant="outline">{region.regularNightCommission}%</Badge>
                            </div>
                          )}
                          {(region.regularHolidayCommission && region.regularHolidayCommission > 0) && (
                            <div className="flex justify-between">
                              <span className="text-sm">Holiday</span>
                              <Badge variant="outline">{region.regularHolidayCommission}%</Badge>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Settings */}
        <div className="mt-6 pt-6 border-t" style={{ textAlign: "left" }}>
          <h4 className="font-medium mb-3">Rule Settings</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm">Minimum Fee</span>
                <Badge variant={undefined} className={rule.minimumFee ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                  {rule.minimumFee ? "Yes" : "No"}
                </Badge>
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm">Auto-adjustment</span>
                <Badge variant={undefined} className={rule.autoAdjustment ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                  {rule.autoAdjustment ? "Enabled" : "Disabled"}
                </Badge>
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm">Negotiation</span>
                <Badge variant={undefined} className={rule.allowNegotiation ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                  {rule.allowNegotiation ? "Allowed" : "Not Allowed"}
                </Badge>
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm">Deposit Required</span>
                <Badge variant={undefined} className={rule.requireDeposit ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                  {rule.requireDeposit ? "Yes" : "No"}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      <AdminHeader />

      <main className="lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Quote Pricing</h1>
                <p className="text-gray-600 mt-1">Manage quote pricing rules and commissions</p>
              </div>
              <Button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Rule
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Active Rules</p>
                    <p className="text-2xl font-semibold mt-1">{quotePricingsTotalCount || 0}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Calculator className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Categories</p>
                    <p className="text-2xl font-semibold mt-1">{new Set(quotePricings.map(rule => rule.category?.name)).size}</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Tag className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Regions</p>
                    <p className="text-2xl font-semibold mt-1">{quotePricings.reduce((total, rule) => total + (rule.regions?.length || 0), 0)}</p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Globe className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Total Revenue</p>
                    <p className="text-2xl font-semibold mt-1">{getDynamicCurrencySymbol()}0</p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b">
              <TabButton
                active={activeTab === "pricing"}
                label="Pricing Rules"
                icon={Calculator}
                onClick={() => setActiveTab("pricing")}
              />
              <TabButton
                active={activeTab === "regions"}
                label="Regions"
                icon={Globe}
                onClick={() => setActiveTab("regions")}
              />
              <TabButton
                active={activeTab === "settings"}
                label="Settings"
                icon={Settings}
                onClick={() => setActiveTab("settings")}
              />
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div className="flex flex-col sm:flex-row gap-4 flex-1">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search pricing rules..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg"
                  />
                </div>
                <Button
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Filter className="h-5 w-5" />
                  Filters
                </Button>
              </div>

              <div className="flex gap-2">
                <CategoryDropdown
                  selectedCategory={selectedCategory}
                  onCategorySelect={setSelectedCategory}
                  placeholder="All Categories"
                  className="min-w-[200px]"
                />

                <AdminRegionDropdown
                  selectedRegion={selectedRegion}
                  onRegionSelect={setSelectedRegion}
                  placeholder="All Regions"
                  className="min-w-[200px]"
                />
              </div>
            </div>

            {/* Loading State */}
            {quotePricingsLoading && (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}

            {/* Error State */}
            {quotePricingsError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                  <span className="text-red-700">{quotePricingsError}</span>
                </div>
              </div>
            )}

            {/* Pricing Rules */}
            {!quotePricingsLoading && !quotePricingsError && (
              <div className="grid grid-cols-1 gap-6">
                {quotePricings.length > 0 ? (
                  quotePricings.map(rule => (
                    <PricingRuleCard key={rule.id} rule={rule} />
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Calculator className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Quote Pricings Found</h3>
                    <p className="text-gray-500">Get started by creating your first quote pricing rule.</p>
                  </div>
                )}
              </div>
            )}

            {/* Pagination */}
            {!quotePricingsLoading && !quotePricingsError && quotePricingsTotalCount > 0 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-500">
                  Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, quotePricingsTotalCount)} of {quotePricingsTotalCount} results
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!quotePricingsHasPreviousPage}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-gray-500">
                    Page {currentPage} of {Math.ceil(quotePricingsTotalCount / 10)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!quotePricingsHasNextPage}
                  >
                    Next
                    <ChevronRightIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Settings Panel - Hidden by default */}
            {activeTab === "settings" && (
              <Card className="p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold">Global Pricing Settings</h3>
                  <p className="text-gray-600 mt-1">Configure default settings for all pricing rules</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium mb-4">Commission Settings</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Default Regular Commission</label>
                        <input type="text" defaultValue="10%" className="mt-1 w-full px-4 py-2 border rounded-lg" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Default Emergency Commission</label>
                        <input type="text" defaultValue="25%" className="mt-1 w-full px-4 py-2 border rounded-lg" />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-4">Quote Settings</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Min Quote Price</label>
                        <input type="number" defaultValue={5} className="mt-1 w-full px-4 py-2 border rounded-lg" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Max Quotes Per Request</label>
                        <input type="number" defaultValue={5} className="mt-1 w-full px-4 py-2 border rounded-lg" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t flex justify-end gap-3">
                  <Button variant="outline">Cancel</Button>
                  <Button>Save Changes</Button>
                </div>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Form Modals */}
      {showAddModal && (
        <QuotePricingForm
          onClose={handleFormClose}
          onSave={handleFormSave}
        />
      )}

      {showEditModal && selectedRule && (
        <QuotePricingForm
          onClose={handleFormClose}
          onSave={handleFormSave}
          editData={selectedRule}
        />
      )}
    </div>
  );
};

export default QuotePricing;